import axios from 'src/utils/axios';
import { AxiosResponse } from 'axios';

import {
  AudienceDefinition,
  AudiencePreview,
  AudienceRowsResponse,
  CampaignDetail,
  CampaignListResponse,
  CampaignRecipientRow,
  ChannelPlanStep,
  FilterOptions,
  RecipientsResponse,
  RecipientRule,
} from 'src/pages/Campaigns/types/Campaigns';

export interface CreateCampaignBody {
  property: number;
  name: string;
  audience?: AudienceDefinition;
  recipient_rule?: RecipientRule;
  respect_pat_gates?: boolean;
  channel_plan?: ChannelPlanStep[];
  template?: number | null;
  email_subject?: string;
  email_body?: string;
  sms_body?: string;
  voice_goal_type?: string;
  voice_language?: string;
  timezone?: string;
  scheduled_at?: string | null;
  previewed_count?: number;
  slack_notify?: boolean;
}

export interface PreviewBody {
  property: number;
  audience?: AudienceDefinition;
  recipient_rule?: RecipientRule;
  respect_pat_gates?: boolean;
  channels?: string[];
}

export interface RowsBody extends PreviewBody {
  search?: string;
  limit?: number;
  offset?: number;
}

const BASE = '/campaigns';

export const CampaignsService = {
  list(
    propertyId: number,
    before?: number
  ): Promise<AxiosResponse<CampaignListResponse>> {
    const params: Record<string, number> = { property: propertyId };
    if (before) {
      params.before = before;
    }
    return axios.get(`${BASE}/`, { params });
  },

  detail(campaignId: number): Promise<AxiosResponse<CampaignDetail>> {
    return axios.get(`${BASE}/${campaignId}/`);
  },

  create(body: CreateCampaignBody): Promise<AxiosResponse<CampaignDetail>> {
    return axios.post(`${BASE}/`, body);
  },

  update(
    campaignId: number,
    body: Partial<CreateCampaignBody>
  ): Promise<AxiosResponse<CampaignDetail>> {
    return axios.patch(`${BASE}/${campaignId}/`, body);
  },

  remove(campaignId: number): Promise<AxiosResponse<void>> {
    return axios.delete(`${BASE}/${campaignId}/`);
  },

  send(
    campaignId: number,
    scheduledAt?: string | null
  ): Promise<AxiosResponse<CampaignDetail>> {
    return axios.post(`${BASE}/${campaignId}/send/`, {
      scheduled_at: scheduledAt ?? null,
    });
  },

  cancel(campaignId: number): Promise<AxiosResponse<CampaignDetail>> {
    return axios.post(`${BASE}/${campaignId}/cancel/`, {});
  },

  retry(campaignId: number): Promise<AxiosResponse<CampaignDetail>> {
    return axios.post(`${BASE}/${campaignId}/retry/`, {});
  },

  recipients(
    campaignId: number,
    params: Record<string, string | number> = {}
  ): Promise<AxiosResponse<RecipientsResponse>> {
    return axios.get(`${BASE}/${campaignId}/recipients/`, { params });
  },

  retryRecipient(
    campaignId: number,
    recipientId: number,
    channel: string
  ): Promise<AxiosResponse<{ delivery_id: number; attempt: number }>> {
    return axios.post(
      `${BASE}/${campaignId}/recipients/${recipientId}/retry/`,
      { channel }
    );
  },

  preview(body: PreviewBody): Promise<AxiosResponse<AudiencePreview>> {
    return axios.post(`${BASE}/audience/preview/`, body);
  },

  audienceRows(body: RowsBody): Promise<AxiosResponse<AudienceRowsResponse>> {
    return axios.post(`${BASE}/audience/rows/`, body);
  },

  filterOptions(propertyId: number): Promise<AxiosResponse<FilterOptions>> {
    return axios.get(`${BASE}/filters/options/`, {
      params: { property: propertyId },
    });
  },

  exportXlsx(
    campaignId: number,
    columns: { id: string; label: string }[]
  ): Promise<AxiosResponse<Blob>> {
    return axios.post(
      `${BASE}/${campaignId}/export/`,
      { columns },
      { responseType: 'blob' }
    );
  },

  uploadAttachment(
    campaignId: number,
    file: File
  ): Promise<
    AxiosResponse<{ id: number; filename: string; url: string | null }>
  > {
    const body = new FormData();
    body.append('file', file);
    return axios.post(`${BASE}/${campaignId}/attachments/`, body);
  },
};

export type RecipientRow = CampaignRecipientRow;
